import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Lightning, ArrowRight, ShieldCheck, Rocket, Code, Users, ChartLineUp,
  CheckCircle, ArrowsLeftRight, BatteryHigh, User, Copy, Sparkle, Storefront,
  WhatsappLogo, EnvelopeSimple, Bank, TrendUp,
} from "@phosphor-icons/react";
import WhatsappBroadcastMockup from "@/components/WhatsappBroadcastMockup";
import EarningSimulator from "@/components/EarningSimulator";
import MagneticButton from "@/components/MagneticButton";
import Reveal from "@/components/Reveal";

const T1 = "https://images.unsplash.com/photo-1621024994278-e409544f4085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMHRha2luZyUyMHBob3RvfGVufDB8fHx8MTc4MzM3NDM5Mnww&ixlib=rb-4.1.0&q=85";
const T2 = "https://images.unsplash.com/photo-1601934025804-c631e2777f26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMHRha2luZyUyMHBob3RvfGVufDB8fHx8MTc4MzM3NDM5Mnww&ixlib=rb-4.1.0&q=85";
const T3 = "https://images.unsplash.com/photo-1512813498716-3e640fed3f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMHRha2luZyUyMHBob3RvfGVufDB8fHx8MTc4MzM3NDM5Mnww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: ShieldCheck, title: "Pix em tempo real", desc: "Depósitos, transferências e saques processados na hora pelo Mercado Pago." },
  { icon: Storefront, title: "Construtor de funil", desc: "Vídeo + planos + captura de leads + entrega automática do produto." },
  { icon: Users, title: "Afiliados até 50%", desc: "Cada produtor define a comissão. Split automático via application_fee." },
  { icon: EnvelopeSimple, title: "Disparo de e-mail", desc: "Campanhas em massa para todos os leads capturados nos seus funis." },
  { icon: Code, title: "API & Webhooks", desc: "Endpoints REST + webhooks para transfer.created, deposit.approved e lead.created." },
  { icon: Sparkle, title: "Sob sua marca", desc: "Painel completo em português, pronto para operar ou revender." },
];

export default function Landing() {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen-safe relative overflow-x-hidden bg-[var(--bg-base)] text-white">
      {/* NAV */}
      <nav className="glass-nav sticky top-0 z-50" data-testid="landing-nav">
        <div className="container-fluid max-w-7xl mx-auto py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <Lightning size={24} weight="fill" color="var(--accent)" />
            <span className="font-display font-black text-lg sm:text-xl tracking-tight">MERCADOPAGU</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              data-testid="landing-login-btn"
              className="text-sm text-white/70 hover:text-white transition-colors px-3 py-2"
            >
              Entrar
            </Link>
            <Link to="/register" data-testid="landing-register-btn">
              <MagneticButton as="span" className="btn-magnetic whitespace-nowrap" strength={0.15} testid="landing-register-cta">
                <span className="hidden sm:inline">Abrir conta grátis</span>
                <span className="sm:hidden">Abrir conta</span>
                <ArrowRight size={14} weight="bold" />
              </MagneticButton>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative hero-grid-bg overflow-hidden">
        <div className="container-fluid max-w-7xl mx-auto py-16 md:py-24 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7"
            >
              <div className="label-caps mb-5 inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--border)] rounded-full bg-white/[0.02] backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--success)] animate-pulse" />
                Mercado Pago Certified · Pix ao vivo
              </div>

              <h1 className="text-fluid-h1 font-display font-black tracking-tight mb-6 wrap-safe">
                O banco digital<br />
                <span className="text-[var(--accent)] relative inline-block">
                  do futuro
                  <motion.span
                    aria-hidden
                    className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[var(--accent)] origin-left"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  />
                </span>,<br />
                sob a sua marca.
              </h1>

              <p className="text-fluid-lead text-white/70 max-w-xl mb-8 leading-relaxed wrap-safe">
                Toda a infraestrutura do <b className="text-white">Mercado Pago</b> com um construtor de funil embutido.
                Seus clientes transferem, recebem Pix e sacam — enquanto você vende infoprodutos com
                <b className="text-[var(--accent)]"> comissão de afiliado até 50%</b>, disparos WhatsApp e área de membros automática.
              </p>

              <div className="flex flex-wrap gap-3 mb-12">
                <Link to="/register" data-testid="hero-cta-primary">
                  <MagneticButton as="span" testid="hero-cta-magnetic">
                    Começar grátis <ArrowRight size={16} weight="bold" />
                  </MagneticButton>
                </Link>
                <a href="#features" className="btn-outline-glow" data-testid="hero-cta-secondary">
                  ver como funciona
                </a>
              </div>

              <div className="grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
                {[
                  { k: "até 50%", v: "comissão afiliado" },
                  { k: "24/7", v: "Pix em tempo real" },
                  { k: "API+Webhook", v: "pronto pra escalar" },
                ].map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                  >
                    <div className="font-display font-black text-[clamp(1.3rem,3vw,2.25rem)] text-[var(--accent)] leading-none">{s.k}</div>
                    <div className="label-caps mt-1.5 text-[10px]">{s.v}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Phone Mockup — mantido tal como antes */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 relative flex justify-center"
              style={{ transform: `translateY(${scrollY * -0.02}px)` }}
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-[var(--accent)]/25 via-[var(--accent)]/10 to-transparent blur-[80px]" />
              <div className="absolute -inset-8 bg-[var(--accent)]/5 rounded-full blur-3xl" />

              <div
                className="relative mx-auto max-w-[280px] sm:max-w-[320px] aspect-[9/19] bg-gradient-to-b from-[#0f0f10] to-[#050506] rounded-[42px] border border-white/10 shadow-2xl p-3 backdrop-blur-xl"
                data-testid="hero-phone-mockup"
                style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.6), 0 0 60px rgba(255,179,15,0.1)" }}
              >
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-10" />

                <div className="relative w-full h-full rounded-[32px] bg-gradient-to-b from-[#0b0b0d] via-[#0d0d0f] to-[#050506] overflow-hidden border border-white/5">
                  <div className="flex items-center justify-between px-6 pt-3 pb-2 text-[10px] font-mono text-white/70">
                    <span>9:41</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px]">••••</span>
                      <BatteryHigh size={13} weight="fill" color="var(--success)" />
                    </div>
                  </div>
                  <div className="px-5 py-4 flex items-center gap-2 border-b border-white/5">
                    <Lightning size={16} weight="fill" color="var(--accent)" />
                    <span className="font-display font-black text-xs tracking-tight">MERCADOPAGU</span>
                    <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-sm border border-[var(--success)] text-[var(--success)] font-mono uppercase leading-none">Pix</span>
                  </div>
                  <div className="px-5 pt-4 pb-2">
                    <div className="text-[10px] label-caps text-white/50 tracking-wider">Saldo disponível</div>
                    <div className="font-display font-black text-2xl tracking-tighter mt-0.5">
                      R$ <span className="text-[var(--accent)]">12.847</span>,90
                    </div>
                  </div>
                  <div className="mx-5 mt-3 p-4 rounded-lg bg-white/5 border border-white/10 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full bg-[var(--accent)]/20 flex items-center justify-center">
                        <ArrowsLeftRight size={12} weight="bold" color="var(--accent)" />
                      </div>
                      <span className="text-[10px] label-caps text-white/60">Transferência Pix</span>
                    </div>
                    <div className="flex items-center gap-2 pb-3 border-b border-white/5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--accent)]/40 to-[var(--accent)]/10 flex items-center justify-center border border-[var(--accent)]/30">
                        <User size={14} weight="fill" color="var(--accent)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-white truncate">Rafael Almeida</div>
                        <div className="text-[9px] text-white/40 font-mono truncate">rafael@mercadopagu.com.br</div>
                      </div>
                      <Copy size={10} className="text-white/30" />
                    </div>
                    <div className="text-center py-3">
                      <div className="text-[9px] label-caps text-white/40 tracking-wider">Valor</div>
                      <div className="font-display font-black text-3xl tracking-tighter text-white mt-0.5">
                        R$ 1.850<span className="text-white/50 text-xl">,00</span>
                      </div>
                      <div className="text-[9px] text-[var(--success)] font-mono mt-1">✓ Sem taxa entre contas MercadoPagu</div>
                    </div>
                    <button className="w-full bg-[var(--accent)] text-black font-bold text-[11px] py-2.5 rounded-md flex items-center justify-center gap-2 hover:bg-[var(--accent)]/90 transition">
                      Transferir agora <ArrowRight size={12} weight="bold" />
                    </button>
                  </div>
                  <div className="px-5 mt-4">
                    <div className="text-[10px] label-caps text-white/40 mb-2">Últimas 24h</div>
                    {[
                      { name: "Camila Santos", value: "+ 320,00", pos: true },
                      { name: "PIX enviado · Diego P.", value: "− 890,50", pos: false },
                    ].map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                        <span className="text-[10px] text-white/70 truncate max-w-[140px]">{t.name}</span>
                        <span className="text-[10px] font-mono font-semibold" style={{ color: t.pos ? "var(--success)" : "rgba(255,255,255,0.6)" }}>
                          R$ {t.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-24 h-1 bg-white/20 rounded-full" />
                </div>
              </div>

              {/* Floating badges — só em desktop pra não estourar em mobile */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.6 }}
                className="hidden md:flex absolute left-0 top-1/3 bg-black/90 backdrop-blur-md border border-[var(--success)]/40 rounded-lg px-3 py-2 items-center gap-2 shadow-xl"
              >
                <CheckCircle size={16} weight="fill" color="var(--success)" />
                <div>
                  <div className="text-[9px] label-caps text-white/50">Pix aprovado</div>
                  <div className="text-[11px] font-display font-bold text-[var(--success)]">R$ 1.850,00</div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.6 }}
                className="hidden md:flex absolute right-0 bottom-16 bg-black/90 backdrop-blur-md border border-[var(--accent)]/40 rounded-lg px-3 py-2 items-center gap-2 shadow-xl"
              >
                <div className="w-7 h-7 rounded-md bg-[var(--accent)]/20 flex items-center justify-center">
                  <ChartLineUp size={13} weight="fill" color="var(--accent)" />
                </div>
                <div>
                  <div className="text-[9px] label-caps text-white/50">Afiliado ganhou</div>
                  <div className="text-[11px] font-display font-bold text-[var(--accent)]">R$ 55,50</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="border-y border-[var(--border)] py-4 overflow-hidden bg-black/40">
        <div className="ticker flex whitespace-nowrap">
          {Array(2).fill(null).map((_, i) => (
            <span key={i} className="font-display font-black text-xl md:text-2xl px-6 text-white/40">
              PIX · TRANSFERÊNCIAS · SAQUE · API · WEBHOOKS · FUNIS · WHATSAPP · E-MAIL · LEADS · AFILIADOS 50% · MERCADO PAGO ·&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* BENTO FEATURES */}
      <section id="features" className="container-fluid max-w-7xl mx-auto py-20 md:py-28">
        <Reveal className="max-w-3xl mb-12 md:mb-16">
          <div className="label-caps mb-4">Recursos</div>
          <h2 className="text-fluid-h2 font-display font-black tracking-tight mb-4 wrap-safe">
            Um banco. Uma esteira de vendas. <span className="text-[var(--accent)]">Uma API.</span>
          </h2>
          <p className="text-fluid-lead text-white/60 wrap-safe">
            Você monta o banco. Nós rodamos em cima do Mercado Pago. Seus clientes usam. Você lucra.
          </p>
        </Reveal>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-4 md:gap-5">
          {/* Simulador de ganhos — destaque */}
          <Reveal className="md:col-span-6 lg:col-span-5 lg:row-span-2" delay={0.05}>
            <EarningSimulator />
          </Reveal>

          {/* Mockup WhatsApp — grande */}
          <Reveal className="md:col-span-6 lg:col-span-7" delay={0.1}>
            <div className="bento-card overflow-hidden">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="w-8 h-8 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center">
                  <WhatsappLogo size={18} color="#25D366" weight="fill" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                    Disparo híbrido
                  </div>
                  <h3 className="text-fluid-h3 font-display font-black text-white mt-0.5">
                    WhatsApp + E-mail, em segundos.
                  </h3>
                </div>
              </div>
              <p className="text-sm text-white/60 mb-4 wrap-safe max-w-lg">
                Baileys (QR Code, sem custo) + Meta Cloud API oficial (templates aprovados). Dispare pra 1 ou 10 mil leads sem sair do painel.
              </p>
              <div className="max-w-md mx-auto">
                <WhatsappBroadcastMockup />
              </div>
            </div>
          </Reveal>

          {/* Cards menores */}
          {FEATURES.map((f, i) => (
            <Reveal
              key={f.title}
              className={i === 0 || i === 3 ? "md:col-span-3 lg:col-span-4" : i === 1 || i === 4 ? "md:col-span-3 lg:col-span-4" : "md:col-span-6 lg:col-span-4"}
              delay={0.15 + i * 0.05}
            >
              <div className="bento-card h-full" data-testid={`bento-feature-${i}`}>
                <f.icon size={28} color="var(--accent)" weight="duotone" />
                <h3 className="font-display font-bold text-lg mt-4 mb-1.5 text-white wrap-safe">{f.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed wrap-safe">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-[var(--border)]">
        <div className="container-fluid max-w-7xl mx-auto py-20 md:py-24">
          <Reveal className="max-w-2xl mb-14">
            <div className="label-caps mb-3">Como funciona</div>
            <h2 className="text-fluid-h2 font-display font-black tracking-tight wrap-safe">
              Da abertura da conta ao <span className="text-[var(--accent)]">lucro</span> em 3 passos.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              { n: "01", t: "Abra sua conta plataforma", d: "Cadastre-se em 30 segundos. Sua conta admin recebe automaticamente as taxas configuradas." },
              { n: "02", t: "Convide seus clientes", d: "Cada cliente abre a própria conta digital. Eles operam Pix, transferem entre si e recebem por links de venda." },
              { n: "03", t: "Taxa transparente pelo serviço", d: "Você cobra uma taxa em cada operação (Pix, transferência, saque). Sem mensalidade — só cobra quando o dinheiro se move." },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bento-card h-full" data-testid={`step-${i}`}>
                  <div className="font-mono text-sm text-[var(--accent)] mb-3">{s.n}</div>
                  <div className="font-display font-bold text-fluid-h3 mb-2 wrap-safe">{s.t}</div>
                  <div className="text-sm text-white/60 leading-relaxed wrap-safe">{s.d}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-[var(--border)]">
        <div className="container-fluid max-w-7xl mx-auto py-20 md:py-24">
          <Reveal className="max-w-2xl mb-14">
            <div className="label-caps mb-3">Empreendedores digitais</div>
            <h2 className="text-fluid-h2 font-display font-black tracking-tight wrap-safe">
              Feito por quem <span className="text-[var(--accent)]">escala</span>.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { img: T1, name: "Rafael M.", role: "Infoprodutor", q: "Ativei afiliados no meu funil com comissão de 30%. Minha rede vende por mim sem eu levantar um dedo." },
              { img: T2, name: "Camila S.", role: "Agência de leads", q: "O funil de captação já entrega leads direto no WhatsApp. Fecho vendas mais rápido." },
              { img: T3, name: "Diego P.", role: "SaaS Founder", q: "A API me permitiu embutir o banco dentro do meu produto. Suporte Mercado Pago é premium." },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bento-card h-full" data-testid={`testimonial-${i}`}>
                  <p className="text-white/85 leading-relaxed mb-6 wrap-safe">"{t.q}"</p>
                  <div className="flex items-center gap-3">
                    <img src={t.img} alt="" className="w-11 h-11 rounded-full object-cover" />
                    <div>
                      <div className="font-semibold text-white">{t.name}</div>
                      <div className="text-xs text-white/50">{t.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="border-t border-[var(--border)] relative overflow-hidden">
        <div className="absolute inset-0 hero-grid-bg opacity-50" />
        <div className="container-fluid max-w-4xl mx-auto py-24 md:py-32 text-center relative">
          <Reveal>
            <div className="halo-accent inline-block mb-6">
              <Lightning size={48} weight="fill" color="var(--accent)" />
            </div>
            <h2 className="text-fluid-h1 font-display font-black tracking-tight mb-6 wrap-safe">
              Pronto para <span className="text-[var(--accent)]">montar seu banco</span> sem burocracia?
            </h2>
            <p className="text-fluid-lead text-white/60 mb-10 max-w-xl mx-auto wrap-safe">
              Sem taxa de setup. Sem contrato. Sem cartório. Você ganha desde o primeiro Pix.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/register" data-testid="cta-bottom-register">
                <MagneticButton as="span" testid="cta-bottom-magnetic">
                  Criar minha conta grátis <ArrowRight size={16} weight="bold" />
                </MagneticButton>
              </Link>
              <Link to="/taxas" className="btn-outline-glow" data-testid="cta-bottom-taxas">
                ver taxas
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-[var(--border)]">
        <div className="container-fluid max-w-7xl mx-auto py-8 flex flex-wrap justify-between gap-4 text-sm text-white/40">
          <div>© 2026 MercadoPagu · Infra Mercado Pago</div>
          <div className="flex gap-6">
            <a href="#features" className="hover:text-white transition-colors">Recursos</a>
            <Link to="/taxas" className="hover:text-white transition-colors" data-testid="footer-link-taxas">Taxas</Link>
            <Link to="/login" className="hover:text-white transition-colors">Entrar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
